import { useAuth0 } from '@auth0/auth0-react';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import { Box, Divider, useMediaQuery } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DrawerFooterProps } from './DrawerFooter.props';
import { LoggedInUser, RoutesEnum, ScreenSize } from '../../../../@types';
import { SAMPLE_LOGIN_USER } from '../../../../constants/user.constant';
import { useGetLoggedInUserData } from '../../../../queries/user-query';

export const DrawerFooter: React.FC<DrawerFooterProps> = ({ toggleDrawer }) => {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [userData, setUserData] = useState<LoggedInUser>(SAMPLE_LOGIN_USER);
  const isMobileView = useMediaQuery(ScreenSize.MOBILE);

  const getLoggedInUserData = useGetLoggedInUserData();

  useEffect(() => {
    if (!getLoggedInUserData.data) return;
    const { userName, profilePic } = getLoggedInUserData.data;
    // Functional update so we don't capture a stale `userData` from the closure
    // — this effect's dep array only watches `getLoggedInUserData.data`.
    setUserData((prev) => ({
      ...prev,
      userName,
      profilePic,
    }));
  }, [getLoggedInUserData.data]);

  const onLogOut = useCallback(() => {
    // Clear in-memory React Query cache before localStorage so cached responses
    // (which may contain customer/service data) aren't observable mid-logout.
    queryClient.clear();
    localStorage.clear();
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout, queryClient]);

  return (
    <Box sx={{ width: '100%', mt: 'auto', mb: '10px', color: 'common.white' }}>
      <Divider sx={{ borderColor: '#334155' }} />
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-around',
          alignItems: 'center',
          pt: '16px',
          pb: '8px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
          <Box
            onClick={() => {
              navigate(RoutesEnum.ProfileRoute, { replace: true });
              isMobileView && toggleDrawer();
            }}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            role="button"
            tabIndex={0}
            aria-label="Go to profile"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                navigate(RoutesEnum.ProfileRoute, { replace: true });
                isMobileView && toggleDrawer();
              }
            }}
          >
            {userData.profilePic && userData.profilePic !== '' ? (
              <Box
                component="img"
                src={userData.profilePic}
                alt="Profile"
                sx={{
                  height: 32,
                  width: 32,
                  borderRadius: '50%',
                }}
              />
            ) : (
              <AccountCircleOutlined fontSize="medium" />
            )}
          </Box>
          <Box sx={{ pl: '8px', minWidth: '80px' }}>{userData.userName}</Box>
        </Box>
        <Box
          onClick={onLogOut}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: '#94a3b8',
            '&:hover': { color: '#fff' },
          }}
          role="button"
          tabIndex={0}
          aria-label="Log out"
          onKeyDown={(e) => e.key === 'Enter' && onLogOut()}
        >
          <LogoutOutlined sx={{ fontSize: 18 }} />
        </Box>
      </Box>
    </Box>
  );
};
